import {useCallback} from 'react'

import {AnalyticsRoutesEnum} from 'interfaces'
import {useTenantResidency} from 'hooks/useTenantResidency'
import {useAnalyticsTenantDomainGate} from './useAnalyticsTenantDomainGate'

export enum AnalyticsRouteRestriction {
  /** The tenant keeps no records, so the view has nothing to show. */
  NoStorage = 'noStorage',
  /** The view is turned off for the tenant's primary domain. */
  TenantUnavailable = 'tenantUnavailable',
  /** Residency is not confirmed yet (loading or failed), so the view stays closed. */
  Unconfirmed = 'unconfirmed',
}

interface UseAnalyticsRouteRestrictionReturn {
  /** Why an Analytics route cannot be opened, or `undefined` when it can. */
  getRouteRestriction: (path: string) => AnalyticsRouteRestriction | undefined
}

/**
 * @description Shared gate for the Analytics route navigation (the overview
 * pills and the route dropdown), so both always agree on which views are
 * closed and why.
 */
export const useAnalyticsRouteRestriction =
  (): UseAnalyticsRouteRestrictionReturn => {
    const {isNoStorageTenant, isStorageTenant, isResidencyPending} =
      useTenantResidency()
    const {isAnalyticsDisabledForPrimaryDomain} = useAnalyticsTenantDomainGate()

    const getRouteRestriction = useCallback(
      (path: string): AnalyticsRouteRestriction | undefined => {
        const isProductivity =
          path === AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY
        const isShadowIT = path === AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT
        const isAIUsage = path === AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS
        const isDomainGated = isProductivity || isAIUsage

        if (!isProductivity && !isShadowIT && !isAIUsage) {
          return undefined
        }
        if (isNoStorageTenant) {
          return AnalyticsRouteRestriction.NoStorage
        }
        if (isDomainGated && isAnalyticsDisabledForPrimaryDomain) {
          return isStorageTenant
            ? AnalyticsRouteRestriction.TenantUnavailable
            : AnalyticsRouteRestriction.Unconfirmed
        }
        // AI Usage also needs a confirmed storage region, so a failed config
        // keeps it closed; the other two only wait while config is loading.
        const isResidencyUnconfirmed = isAIUsage
          ? !isStorageTenant
          : isResidencyPending
        return isResidencyUnconfirmed
          ? AnalyticsRouteRestriction.Unconfirmed
          : undefined
      },
      [
        isAnalyticsDisabledForPrimaryDomain,
        isNoStorageTenant,
        isResidencyPending,
        isStorageTenant,
      ],
    )

    return {getRouteRestriction}
  }
